import React, { useState } from "react";
import cx from "classnames";
import { useUserProgression } from "@/hooks/useUserProgression";
import {
  Caret,
  Clamp,
  Clock2Icon,
  CloseIcon,
  Collapse,
  Skeleton,
  SmallPromoIcon,
  TickIcon,
} from "@picklebet/ui";
import { ViewTermsLink } from "@/features/promotions/components/ViewTermsLink";
import { PerkIcon } from "@/features/perks/components/PerkIcon";
import type { ProcessedBetEntry } from "@/hooks/firestore/betting/useEntries";
import * as styles from "./BetSelectionAutomation.module.scss";

type BetSelectionAutomationProps = {
  entry: ProcessedBetEntry;
  progressionId: string;
};

const ProgressionStatusClamp = ({
  progressionStatus,
  isOpen,
}: {
  isOpen: boolean;
  progressionStatus: "PAID" | "NOT_PAID" | "IN_PROGRESS";
}) => (
  <Clamp
    variant="warning"
    className={cx(styles.statusClamp, {
      [styles.paid]: progressionStatus === "PAID",
      [styles.notPaid]: progressionStatus === "NOT_PAID",
      [styles.inProgress]: progressionStatus === "IN_PROGRESS",
    })}
  >
    <span className={styles.iconWrapper}>
      {progressionStatus === "PAID" && <TickIcon />}
      {progressionStatus === "NOT_PAID" && (
        <CloseIcon className={styles.close} />
      )}
      {progressionStatus === "IN_PROGRESS" && (
        <Clock2Icon className={styles.clock} />
      )}
    </span>
    <span className={styles.clampText}>
      {progressionStatus.replace("_", " ")}
    </span>
    <span>
      <Caret className={styles.caret} variant={isOpen ? "up" : "down"} />
    </span>
  </Clamp>
);

const BetSelectionAutomation = ({
  progressionId,
  entry,
}: BetSelectionAutomationProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const {
    campaign,
    isComplete,
    isFailed,
    loading,
    displayPayout,
    progression,
    hide,
  } = useUserProgression(progressionId, entry);

  const type = progression?.actions?.[0]?.type;

  const progressionStatus = isComplete
    ? isFailed
      ? "NOT_PAID"
      : "PAID"
    : "IN_PROGRESS";

  if (hide) return null;

  return (
    <div
      className={cx(styles.container, {
        [styles.notPaid]: progressionStatus === "NOT_PAID",
      })}
      onClick={(e) => {
        e.preventDefault();
        setIsOpen((visible) => !visible);
      }}
    >
      {loading || !progression ? (
        <Skeleton />
      ) : (
        <>
          <div className={styles.promoIconsContainer}>
            <div
              className={cx(styles.promoIcons, {
                [styles.notPaid]: progressionStatus === "NOT_PAID",
              })}
            >
              <SmallPromoIcon
                className={styles.promoIcon}
                width={"20"}
                height={"20"}
                variant={"enclosed"}
              />
              {type &&
                (type === "WINNINGS_BOOST" || type === "STAKE_REFUND") && (
                  <PerkIcon
                    type={type}
                    className={cx(styles.perkIcon, {
                      [styles.winningsBoost]: type === "WINNINGS_BOOST",
                    })}
                  />
                )}
            </div>
          </div>
          <div className={styles.content}>
            <div>
              {campaign?.name && (
                <div className={styles.name}>{campaign.name}</div>
              )}

              {!isFailed && (
                <div className={styles.payout}>{displayPayout}</div>
              )}
            </div>

            <ProgressionStatusClamp
              progressionStatus={progressionStatus}
              isOpen={isOpen}
            />
          </div>
          <Collapse isOpen={isOpen}>
            <div
              className={cx(styles.dashedLine, {
                [styles.notPaid]: progressionStatus === "NOT_PAID",
              })}
            />
            <div className={styles.content}>
              <div className={styles.description}>
                {campaign?.description}
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <ViewTermsLink
                    className={styles.link}
                    link={campaign?.termsOfUse}
                    isNotPaid={progressionStatus === "NOT_PAID"}
                  />
                </span>
              </div>
            </div>
          </Collapse>
        </>
      )}
    </div>
  );
};

export { BetSelectionAutomation };
